import Card from './Card';
import Champion from './Champion';
import Companion from './Companion';
import Room from './Room';
import Fight from './Fight';
import Map from './Map';
import Library from '../utility/Library';
import User from '../../User';

export default class Run {

	init (broadcaster, champion, difficulty, params={}) {

		const data = User.dataFor(champion, difficulty);
		this.contentLimits = User.contentLimits(champion);
		this.difficultySetting = difficulty;
		this.power = Math.min(this.contentLimits.mazePower, params.power || data.devotion.maze.selected);
		this.champion = new Champion(this, champion, params);
		this.companion = params.companion ? new Companion(this, params.companion) : null;
		this.startingFloor = 1;
		this.deck = params.deck ? params.deck.filter(key => {
			const card = Library.cards[key];
			return card && (!card.level || card.level <= data.level);
		}).map(key => { return {card: this.getUpgradedCard(key, true), status: "enabled"}}) : this.buildStartingDeck(this.champion);
		this.floors = [];
		this.broadcaster = broadcaster;
		this.minDeckSize = 12;
		this.cardChance = 0.5;
		this.eventChance = 0;
		this.maxFloor = 3;
		this.roomsCleared = 0;
		this.bossesSlain = 0;
		this.entropy = 0;
		this.paws = 0;
		this.keys = 0;
		//this.insight = 3;
		//this.removes = 5;
		this.addedBenevolence = 0;
		this.convertedExpBonus = 0;
		this.time = 17;
		this.rooms = [];
		this.plan = [];
		this.floor = 1;
		this.act = null;
		this.usedFights = [];
		this.usedEvents = [];
		this.usedCelestials = [];
		this.nextCelestialKey = null;
		this.cardChoiceCount = 0;
		this.pityTimerRare = 0;
		this.pityTimerPotion = 0;
		this.pityTimerRelicEvent = 0;
		this.seedCountShop = 0;
		this.removeCountShop = 0;
		this.quest = this.data.quest;
	}

	get traits () {

		return Object.fromEntries(Object.entries(User.dataFor(this.champion.model.key, this.difficultySetting).devotion).map(([key, value]) => [key, value.selected]));
	}

	get data () {

		return User.databank[this.champion.model.key][this.difficultySetting];	
	}

	start () {

		this.notify('gamestate', this.serialize());
		this.newFloor();
		/*if (this.deckbuilder) {
			this.floor.push(new Room(this, { type: "deckbuilder" }));
		}
		else {
			this.notify("startrun", this);
			this.newRoom(this.map.shift()[0]);
		}*/
		let room = new Room(this, this.map.start);
		this.notify("startrun", this);
		room.enter(false);
	}

	get deckSize () {

		return this.deck.filter(card => card.status !== "disabled" && !card.card.locked && !Library.hasSubtype(card.card, "augment")).length;
	}

	get canCloseDeckbuilder () {

		return this.deckSize >= this.minDeckSize || this.deck.filter(card => card.status === "disabled" && !card.card.locked && !Library.hasSubtype(card.card, "augment")).length === 0;
	}

	changeMinDeckSize (value) {

		this.minDeckSize = Math.max(0, this.minDeckSize + value);
		this.notify("mindecksize", this, value);
	}

	diveIn () {

		if (this.floor >= this.maxFloor)
			return;
		this.floor++;
		this.newFloor();
		this.time = 17;
		let room = new Room(this, this.map.start);
		this.notify("divein", this);
		room.enter(false);
	}

	availableCelestials () {

		const celestials = ["amaryllis", "nikara", "aurora", "zephyrus"];
		let available = celestials.filter(celestial => !this.usedCelestials.includes(celestial));
		if (available.length === 0)
			available = celestials;
		return available;
	}

	previewCelestial () {

		// Nilith advances saved progression, even beyond this release's playable cap.
		if (this.floor === 3 && this.power === this.data.devotion.maze.rank)
			return "nilith";
		if (!this.nextCelestialKey) {
			const available = this.availableCelestials();
			this.nextCelestialKey = available[Math.floor(Math.random() * available.length)];
			this.notify("previewcelestial", this, this.nextCelestialKey);
		}
		return this.nextCelestialKey;
	}

	nextCelestial () {

		const celestial = this.previewCelestial();
		if (celestial !== "nilith" && !this.usedCelestials.includes(celestial))
			this.usedCelestials.push(celestial);
		this.nextCelestialKey = null;
		return celestial;
	}

	enterCelestialRoom () {

		let room = new Room(this, {type: this.nextCelestial(), afterBoss: true});
		room.enter(false);
	}

	changeTime (time) {

		if (time < 0 && this.time + time < 0)
			time = -this.time;
		if (time === 0)
			return;
		this.time += time;
		this.notify("changetime", this);
	}

	get room () {

		return this.rooms[this.rooms.length-1]
	}

	get nextRooms () {

		return this.map.sections[this.map.sections.length-this.time]?.filter(r => r && (this.room.model.model || this.room.model).next?.includes(r.index)) || [];
	}

	continueTo (index) {

		if (this.room?.type === "start" && this.room.collectables.some(collectable => collectable))
			return;

		let room = new Room(this, this.nextRooms[index]);
		room.enter(false);
	}

	setPlan (plan) {

		this.plan = plan.slice();
		this.notify("setplan", this);
	}

	get deckbuilder () {

		return false; //this.depth < this.startingFloor;
	}

	get deathShardReward () {

		if (!this.over || this.storyEvent)
			return 0;
		return this.convertedExpReward;
	}

	get convertedExpReward () {

		if (this.storyEvent)
			return 0;
		return (this.convertedExpBonus || 0) + (this.over ? this.currentExpShardReward : 0);
	}

	get currentExpShardReward () {

		return Math.ceil(Math.max(0, this.champion.gatheredExp) / 2);
	}

	get expMultiplier () {

		return 1;
	}

	getKey () {

		this.notify("key", this);
		this.victory();
	}

	getPower () {

		this.notify("power", this);
		this.victory();
	}

	/*next () {

		if (this.deckbuilder) {
			while (this.depth < this.startingFloor)
				this.newFloor();
			this.setupPathChoice();
			return;
		}
		if (this.storyEvent) {
			this.victory();
			return;
		}
		if (this.map.length <= 0) {
			if (this.depth >= this.maxFloor) {
				this.victory();
				return;
			}
			this.newFloor();
		}
		\/*this.newRoom(this.roomChoices()[0]);
		this.room.enter();*\/
		\/*this.campfireChoices = ["rest", "explore"];
		if (this.champion.level < this.champion.maxLevel)
			this.campfireChoices.push("worship");
		this.notify('campfire', this, this.campfireChoices);*\/
		this.setupPathChoice();
	}*/

	chooseCampfire (choice) {

		switch (choice) {
		case "rest":
			this.champion.heal(this.restHealing);
			break;
		/*case "explore":
			this.cardChoice();
			return;*/
		case "worship":
			this.champion.loseCorruption(this.worshipCorruption);
			this.champion.gainExp(this.worshipExp);
			/*let lvup = this.champion.checkLevelUp();
			if (lvup)
				return;*/
			break;
		default: break;
		}

		delete this.campfireChoices;
		this.notify('choosecampfire', this);
		this.setupPathChoice();
	}

	get seedExp () { return Math.ceil(15 * this.floor * Math.pow(this.power, 2) * this.expMultiplier) + this.champion.getPassive("seedexp"); }
	get berryHp () { return (15 + 5 * this.floor) * this.power + this.champion.getPassive("berryhp"); }
	get coinGold () { return 10 + 10 * this.floor + this.champion.getPassive("coingold"); }

	get restHealing () { return Math.ceil(Math.min(this.champion.maxhp - this.champion.hp, (1 + this.champion.getPassive("fountainheal")) * this.champion.maxhp * (this.zen ? 1 : (this.casual ? 0.4 : 0.3)))); }
	get worshipExp () { return Math.ceil(this.floor * 25 * Math.pow(this.power, 2) * this.expMultiplier); }
	get worshipCorruption () { return Math.min(20, this.champion.corruption);/*Math.min((this.floor * 10) * (0.9 + 0.1 * this.traits.fountainExp), this.champion.corruption)*/; }

	gameOver () {

		if (this.win || this.over)
			return;

		this.over = true;
		this.notify('gameover', this);
	}

	victory () {

		if (this.win || this.over)
			return;

		this.win = true;
		this.notify('victory', this);
	}

	get stats () {

		return {
			champion: this.champion.model.key,
			level: this.champion.levelReached
		}
	}

	buildStartingDeck (champion) {

		/*if (this.startingFloor > 2)
			return [];
		if (this.startingFloor > 1)
			return [
				Library.getCard(champion.model.startingDeck[0]),
				Library.getCard(champion.model.startingDeck[7]),
				Library.getCard(champion.model.startingDeck[13]),
				Library.getCard(champion.model.startingDeck[14])
				]*/

		return champion.model.startingDeck.map(key => { return { card: this.getUpgradedCard(key, true), status: "enabled" }});
	}

	getUpgradedCard (card, isKey=false) {

		if (isKey)
			return Library.getUpgradedCard(card);
		return Library.upgradeCard(card);
		/*let tier = this.data.upgrades[card.key];
		return tier ? Object.assign({}, card, {tier}, card.upgrades[tier-1]) : card;*/
	}

	addCard (model, askRemove=true) {

		this.deck.push({card: model, new: !model.starter, status: "enabled"});

		if (this.champion.getPassive("addcardgold"))
			this.champion.gainGold(this.champion.getPassive("addcardgold"));
		if (this.champion.getPassive("addcardexp"))
			this.champion.gainExp(this.champion.getPassive("addcardexp") * this.floor);
		if (this.champion.getPassive("duplicatenewcards") && !this.duplicatingNewCard && (model.rarity === 1 || model.rarity === 2)) {
			this.duplicatingNewCard = true;
			this.addCard(model, false);
			delete this.duplicatingNewCard;
		}
		/*if (this.entropy)
			this.entropy--;*/
		if (!model.token)
			User.learn("cards", model.key);

		this.notify('addcard', this, model);

		//if (this.removes > 0 && askRemove)
		//	this.dropChoice();

		/*if (this.room?.type === "fight" && this.room.fight.done) {
			this.room.cleanup();
		}
		if (this.deckbuilder && this.room.type !== "shop") {
			if (this.deck.length >= 15) {
				this.newRoom({ type: "shop", merchant: "Mezuri" });
				this.room.enter();
			} else {
				if (this.deck.length === 14) {
					let options = this.cardChoiceLevel(this.champion.level);
					this.notify('newcardchoice', this, options);
				}
				else if (this.deck.length < 6 || (this.deck.length < 4 && this.startingFloor === 2))
					this.cardChoice(Math.max(1, this.champion.level-2));
				else
					this.cardChoice(this.champion.level-1);
			}
		} else
			this.dropChoice();*/
	}

	removeCard (n) {

		this.deck.splice(n, 1);
		this.notify('removecard', this, n);
	}

	switchEnableCard (n) {

		if (this.deck[n].card.locked)
			return;

		if (this.deck[n].status !== "disabled") {
			if (this.deckSize <= this.minDeckSize && !this.deck[n].card.locked && !Library.hasSubtype(this.deck[n].card, "augment"))
				return;
			this.deck[n].status = "disabled";
		}
		else if (this.deck[n].status === "disabled")
			this.deck[n].status = "enabled";
		else return;
		this.notify('enablecard', this, n);
	}

	/*setupMap () {

	    const isStartingFloor = this.startingFloor === this.floor;
		this.map = Array.from({ length: 16 }, () => []);
		this.map[0].push({ type: "shop", difficulty: "easy" });
		this.map[1].push({ type: "fight", difficulty: "easy" });
		this.map[2].push({ type: "fight", difficulty: "easy" });
		this.map[14].push({ type: "fountain" });
		this.map[15].push({ type: "fight", difficulty: "boss" });

		let elite1 = Math.floor(Math.random() * 5) + 7;
	    let elite2;
	    do {
	        elite2 = Math.floor(Math.random() * 5) + 7;
	    } while (Math.abs(elite1 - elite2) < 2);
	    this.map[elite1].push({ type: "fight", difficulty: "elite" });
	    this.map[elite2].push({ type: "fight", difficulty: "elite" });

		let fountain;
	    do {
	        fountain = isStartingFloor ? Math.floor(Math.random() * 6) + 5 : Math.floor(Math.random() * 10) + 1;
	    } while (elite2 === fountain);

		let shop1;
	    do {
	        shop1 = isStartingFloor ? Math.floor(Math.random() * 5) + 9 : Math.floor(Math.random() * 11) + 3;
	    } while (elite2 === shop1 || this.map[shop1].length > 1);
		this.map[shop1].push({ type: "shop" });
	    if (Math.random() < isStartingFloor ? 0.25 : 0.5) {
	    	let shop2;
		    do {
		        shop2 = isStartingFloor ? Math.floor(Math.random() * 5) + 9 : Math.floor(Math.random() * 11) + 3;
		    } while (Math.abs(shop1 - shop2) < 2 || shop2 === elite2 || this.map[shop2].length > 1);
			this.map[shop2].push({ type: "shop" });
		}

	    for (let i = 1; i < 14; i++) {
	    	if (this.map[i].length === 1) {
	    		if (i !== elite2 && Math.random() < 0.6) {
	    			if (this.map[i][0].type === "fight" && this.map[i][0].difficulty === "easy")
	    				this.map[elite1].push({ type: "fight", difficulty: "medium" });
	    			else
	    				this.map[elite1].unshift({ type: "fight", difficulty: "medium" });
	    		}
	    	} else if (this.map[i].length === 0)
	    		this.map[i].push({ type: "fight", difficulty: "medium" });
	    }

	    let itemlist = Object.values(Library.items).filter(i => i.floors && i.floors.includes(this.floor+1) && !i.champion || i.champion === this.champion.model.key || Array.isArray(i.champion) && i.champion.includes(this.champion.model.key));
	    this.map.forEach(f => f.filter(room => room.type === "fight").forEach(params => {
	    	let points = { easy: 6, medium: 12, elite: 18, boss: 30 }[params.difficulty] * (this.floor+1) * (0.8 + Math.random() * 0.4);
	    	let expratio = 0.4 + Math.random() * 0.2;
	    	let exp = Math.round(points * expratio * 1.2);
	    	points -= points * expratio;
	    	const difficultyToItemChance = { easy: 0, medium: 0.2, elite: 0.8, boss: 1 }
	    	let possibleItems = itemlist.filter(i => i.points < 0.6 * points);
	    	let item = undefined;
	    	if (possibleItems.length > 0 && Math.random() < difficultyToItemChance[params.difficulty]) {
	    		item = possibleItems[Math.floor(Math.random() * possibleItems.length)];
	    		points -= item.points;
	    	}
	    	let gold = Math.round(points * 3);
	    	params.reward = { exp, gold };
			if (item)
			    params.reward.item = item;
	    }))
	}*/

	newFloor () {

		this.chooseAct();
		this.map = new Map(this);
		this.map.generate();
		this.plan = [];
		this.usedFights = [];
		this.timeOnFloor = 0;
		if (!this.storyEvent) {
			/*while (this.entropy)
				this.addCard(Library.getCard(-21));*/
			if (this.floor !== this.maxFloor)
				this.entropy = 0;
		}
		//this.setupMap();
		//this.champion.heal(this.zen ? this.champion.maxhp : (this.casual ? 100 : 50) * this.depth);
		//this.floors.push([]);
		//this.time = 15;
		this.notify('newfloor', this);
	}

	chooseAct () {

		let acts = Object.values(Library.acts).filter(act => act.act === this.floor);
		if (acts.length === 0)
			acts = Object.values(Library.acts);
		this.act = acts[Math.floor(Math.random() * acts.length)]?.key;
	}

	get actModel () {

		return Library.getAct(this.act) || Object.values(Library.acts).find(act => act.act === this.floor);
	}

	get difficulty () {

		return this.difficultySetting;
	}

	get zen () {

		return this.difficultySetting === "zen";
	}

	get casual () {

		return this.difficultySetting === "casual";
	}

	get challenge () {

		return this.difficultySetting === "challenge";
	}

	get hardcore () {

		return this.difficultySetting === "hardcore";
	}

	setupPathChoice () {

		/*let choices = [];
		if (this.time >= 10) {
			choices.push({ type: "fight", difficulty: "easy" });
		} else if (this.time >= 0) {
			choices.push({ type: "fight", difficulty: "medium" });
		} else if (this.time === 0) {
			choices.push({ type: "fight", difficulty: "boss" });
		}

		this.pathChoices = choices;*/
		this.pathChoices = this.map.shift();
		this.notify("pathchoices", this, this.pathChoices);
	}

	choosePath (choice) {

		delete this.pathChoices;
		this.notify("choosepath", this, choice);
		this.newRoom(choice);
		this.room.enter()
	}

	pickRandomRoom(rooms) {
	    // Calculate the total sum of frequencies
	    const totalFrequency = rooms.reduce((sum, room) => sum + (room.frequency || 1), 0);
	    
	    // Generate a random number between 0 and the totalFrequency
	    let random = Math.random() * totalFrequency;
	    
	    // Loop through the rooms and subtract their frequencies from the random number
	    for (let i = 0; i < rooms.length; i++) {
	        random -= rooms[i].frequency || 1;
	        if (random < 0) {
	            return rooms[i];
	        }
	    }
	}

	generateRelic () {

		let relics = Object.values(Library.relics);
		relics = relics.filter(relic => relic.pool === "treasure" && (!relic.champion || relic.champion === this.champion.model.key))
		relics = relics.filter(relic => !this.champion.relics.some(r => r.key === relic.key || (r.conflict && r.conflict.includes(relic.key))))

		const weightedArray = relics.flatMap(item => {
	    	let weight = 0;
	        if (item.rarity === 2) weight = 2;
	        else if (item.rarity === 3) weight = 1;
	        else weight = 3;

	        return Array(weight).fill(item);
	    });

		let relic = weightedArray[Math.floor(Math.random() * weightedArray.length)];
	    return Library.upgradeItem(relic, this.power);
	}

	/*newRoom (params) {

		if (params.type === "fountain") {
			this.floor.push(new Room(this, { type: "fountain" }));
			return;
		}

		if (params.type === "shop") {
			this.floor.push(new Room(this, { type: "shop" }));
			return;
		}

		let rooms = Object.values(Library.rooms);
		rooms = rooms.filter(r => r.type === params.type && r.floor === this.floor);	
		rooms = rooms.filter(r => r.difficulty === params.difficulty);
		if (params.type === "fight")
			rooms = rooms.filter(r => this.rooms.filter(a => a.model.key === r.key).length === 0);
		if (params.merchant)
			rooms = rooms.filter(r => r.merchant === params.merchant);
		if (!this.challenge)
			rooms = rooms.filter(r => !r.challenge);
		let room = this.pickRandomRoom(rooms);

		this.floor.push(new Room(this, room, params.reward));
	}*/

	generateCardChoices (nb=3, rare=false, customRarity=null, hard=false) {

		/*let rarityByFloor = [
			[1,0,0],
			[1,1,0],
			[1,1,0],
			[3,6,1]
		]

		let rarityByFloorHard = [
			[1,1,0],
			[0,1,0],
			[0,3,1],
			[0,0,1]
		]*/

		let rarityByFloor = [
			[90,10,0],
			[66,33,1],
			[31,64,5]
		]

		let rarity = customRarity || rarityByFloor[this.floor-1];

		let options = [];

	    const weightedArray = this.champion.cardpool.filter(card => !card.starter && !card.token && card.rarity).flatMap(item => {
	    	let weight = 0;
	    	if (rare)
	    		weight = item.rarity === 3 ? 1 : 0;
	    	else {
		        weight = rarity[item.rarity-1];
	    	}

	        return Array(weight).fill(item);
	    });
	    
		for (let i = 0; i < nb; i++) {
			let option = null;
			while (option === null || options.includes(option)) {

			    option = weightedArray[Math.floor(Math.random() * weightedArray.length)];
			}
			options.push(option);
		}

		options = options.map(option => this.getUpgradedCard(option))
		return(options);
	}

	cardChoice (fight, nb=3, rare=false, hard=false) {

		this.choosingCard = true;

		this.options = this.generateCardChoices(nb, rare, null, hard);
		this.reroll = rare || this.champion.getPassive('cardreroll');
		this.options.forEach(option => {
			if (!option.token)
				User.learn("cards", option.key);
		})
		this.notify('newcardchoice', this, this.options, this.reroll);
	}

	storeCardChoice () {

		if (!this.choosingCard)
			this.cardChoice(true, 3, false);
		else
			this.storedCardChoice = 1;
	}

	cardBundle () {

		this.choosingCard = true;
		this.bundle = true;

		this.options = this.generateCardChoices(3, false, [1,0,0]);
		this.notify('newcardbundle', this, this.options);
	}

	cardChoiceWith (options, rare=false) {

		this.choosingCard = true;

		this.reroll = rare || this.champion.getPassive('cardreroll');
		this.options = options;
		this.options.forEach(option => {
			if (!option.token)
				User.learn("cards", option.key);
		})
		this.notify('newcardchoice', this, this.options, this.reroll);
	}

	cardChoiceLevel (level) {

		this.choosingCard = true;

		let cards = User.cardsFor(this.champion.model.key, this.difficulty);
		cards = cards.filter(c => c.rarity === 3);
		let options = [];
		for (let i = 0; i < 1; i++) {
			let option = null;
			while (option === null || options.includes(option))
				option = cards[Math.floor(Math.random() * cards.length)]
			options.push(option)
		}

		options = options.map(option => this.getUpgradedCard(option))
		this.options = options;
		return options;
	}

	levelUp () {

		//this.levelingUp = true;
		//delete this.reroll;
		//return this.cardChoiceLevel(this.champion.level);
	}

	collectCard (n) {

		if (!this.options || !this.choosingCard)
			return;

		let choice = this.options[n];

		delete this.options;
		delete this.choosingCard;

		this.notify('collectcard', this, n);
		this.addCard(choice);
		this.resolveCardChoiceEffects();

		if (this.storedCardChoice) {
			this.storedCardChoice--;
			this.cardChoice(true, 3, false)
		}
	}

	dropChoice (free=false) {

		if (this.deck.length === 0)
			return;

		this.droppingCard = free ? "free" : true;

		this.notify('dropcardchoice', this, free);
	}

	duplicateChoice () {

		if (this.deck.length === 0)
			return;

		this.duplicatingCard = true;

		this.notify('duplicatecardchoice', this);
	}

	addRandomCard (n=1, filter) {

	    let cards = this.champion.cardpool;
	    if (filter)
	    	cards = cards.filter(filter);
		for (let i = 0; i < n; i++)
			this.addCard(this.getUpgradedCard(cards[Math.floor(Math.random() * cards.length)]));
	}

	skipChoice () {

		/*if (this.levelingUp && !this.reroll) {
			this.reroll = true;
			let previousOption = this.options[0];
			while (this.options[0] === previousOption)
				this.cardChoiceLevel(this.champion.level);
			this.notify("reroll", this, this.options);
			return;
		}*/

		if (this.bundle) {

			this.options.forEach(o => this.addCard(o));
			delete this.options;
			delete this.choosingCard;
			delete this.reroll;
			delete this.bundle;
			this.notify('skipcardchoice', this);
		}
		else if (this.reroll) {
			delete this.reroll;
			if (this.options.length === 1) {
				let previousOption = this.options[0]
				while (this.options[0].key === previousOption.key)
					this.options = this.generateCardChoices(1, true);
			} else
				this.options = this.generateCardChoices(this.options.length);
			this.notify('newcardchoice', this, this.options, false);
			this.notify("reroll", this, this.options);
			return;
		}/* else if (this.removes < 1 && !this.champion.getPassive("cardremovalskip")) {
			return;
		}*/

		//if (this.options.length > 1 && !this.champion.getPassive("cardremovalskip"))
		//	this.removes--;
		delete this.options;
		delete this.choosingCard;
		this.notify('skipcardchoice', this);
		this.resolveCardChoiceEffects();
		
		if (this.champion.getPassive("skipmaxhp"))
		    this.champion.gainMaxHp(this.champion.getPassive("skipmaxhp"));
		if (this.champion.getPassive("skipmystery")) {
		    this.addRandomCard(1, c => c.rarity && c.rarity === 1);
		    this.addRandomCard(1, c => c.rarity && c.rarity === 2);
		}

		/*if (this.levelingUp) {

			delete this.room.choices;
			delete this.levelingUp;
			this.room.cleanup();
		} else if (this.room?.choices && this.room?.type === "fight") {

			delete this.room.choices;
			this.room.startFight();
		}

		if (this.campfireChoices) {

			delete this.campfireChoices;
			if (this.room)
				delete this.room.choices;
			this.notify('choosecampfire', this);
			this.setupPathChoice();
		}

		if (this.room?.type === "fight" && this.room.fight.done) {
			this.room.cleanup();
		}*/
	}

	resolveCardChoiceEffects () {

		if (!this.pendingCardChoiceEffects)
			return;

		const effects = this.pendingCardChoiceEffects;
		delete this.pendingCardChoiceEffects;

		if (effects.entropy)
			this.changeMinDeckSize(effects.entropy);
	}

	dropCard (n) {

		if (!this.droppingCard)
			return;

		if (n >= 0) {
			this.removeCard(n);
			/*if (this.droppingCard !== "free")
				this.removes--;*/
		}

		delete this.droppingCard;
		this.notify('dropcard', this, n);

		/*if (this.levelingUp) {

			delete this.levelingUp;
		}
		if (this.campfireChoices) {

			delete this.campfireChoices;
			this.notify('choosecampfire', this);
			this.setupPathChoice();
		}

		if (this.room?.type === "fight" && this.room?.fight.done || this.room?.type === "fountain") {
			this.room.cleanup();
		}*/
	}

	duplicateCard (n) {

		if (!this.duplicatingCard)
			return;

		if (n >= 0)
			this.addCard(this.deck[n].card, false);

		delete this.duplicatingCard;
		this.notify('duplicatecard', this, this.deck[n]);
	}

	addRemoves (value) {

		this.removes += value;
		this.notify('removes', this, value)
	}

	addInsight (value) {

		//this.insight += value;
		//this.notify('insight', this, value)
	}

	addPaws (value) {

		this.paws += value;
		this.notify('paws', this, value)
	}

	addKeys (value) {

		this.keys += value;
		this.notify('keys', this, value)
	}

	addBenevolence (value) {

		this.addedBenevolence += value;
	}

	get benevolence () {

		return this.data.benevolence + this.addedBenevolence;
	}

	/*save () {

		delete this.roomSave;
		this.roomSave = this.serialize();
	}

	reload () {

		if (!this.roomSave)
			return;
		let copy = Run.build(this.roomSave, null);
		Object.keys(copy).filter(key => key !== "roomSave" && key !== "broadcaster").forEach(key => this[key] = copy[key]);
		this.notify('reload', this);
		this.room.enter();
	}*/

	startStoryEvent (enemies, background, audio, minDeckSize, memory=false) {

		delete this.win;
		if (this.room && this.room.fight)
			this.room.fight.cleanup();
		this.storyEvent = true;
		this.champion.gold = 0;
		//this.insight = 0;
		this.minDeckSize = minDeckSize || 12;
		//this.removes = 0;
		this.background = background;
		this.audio = audio;
		this.memory = memory;
		this.notify("storyevent", this, background, audio, memory);
		//this.champion.heal(this.champion.maxhp);
		//this.floors.push([]);
		//this.notify('newfloor', this);
		let room = new Room(this, {
			"key": -1,
			"type": "story",
			"enemies": enemies
		});
		room.enter();
	}

	continue () {

		if (!this.over)
			return;
		const room = this.room;
		// Defeat normally cleans up first; also handle game-over from other sources.
		if (room?.fight && !room.fight.done)
			room.fight.cleanup();
		delete this.over;
		this.notify('continue', this);
		if (room?.fight)
			room.fight = new Fight(this);
		// Revival restores health unconditionally, including with healing curses.
		const restoredHp = this.champion.maxhp - this.champion.hp;
		this.champion.hp = this.champion.maxhp;
		this.notify('lifegain', this.champion, restoredHp);
		if (room?.fight)
			room.fight.start(room.model.slots || room.model.enemies);
	}

	notify (type, ...data) {

		if ((this.over && type !== "gameover") || (this.win && type !== "victory"))
			return;
		if (this.broadcaster)
			this.broadcaster.notify(type, data);
	}

	subscribe (type, notify) {

		if (this.broadcaster)
			return this.broadcaster.subscribe(type, notify);
		return () => {};
	}

	serialize () {

		return {
			power: this.power,
			contentLimits: this.contentLimits,
			champion: this.champion.serialize(),
			companion: this.companion?.serialize(),
			map: this.map?.serialize(),
			floor: this.floor,
			act: this.act,
			rooms: this.rooms.map(room => room.serialize()),
			deck: this.deck.map(card => Object.assign({}, card)),
			levelingUp: this.levelingUp,
			choosingCard: this.choosingCard,
			droppingCard: this.droppingCard,
			duplicatingCard: this.duplicatingCard,
			storedCardChoice: this.storedCardChoice,
			options: this.options ? this.options.map(option => option.key) : null,
			cardChance: this.cardChance,
			eventChance: this.eventChance,
			maxFloor: this.maxFloor,
			storyEvent: this.storyEvent,
			memory: this.memory,
			background: this.background,
			audio: this.audio,
			roomsCleared: this.roomsCleared,
			bossesSlain: this.bossesSlain,
			difficultySetting: this.difficultySetting,
			//insight: this.insight,
			//removes: this.removes,
			paws: this.paws,
			keys: this.keys,
			time: this.time,
			startingFloor: this.startingFloor,
			campfireChoices: this.campfireChoices,
			pathChoices: this.pathChoices,
			droppingPotion: this.droppingPotion,
			droppingRelic: this.droppingRelic,
			quest: this.quest,
			cardChoiceCount: this.cardChoiceCount,
			pityTimerRare: this.pityTimerRare,
			pityTimerPotion: this.pityTimerPotion,
			pityTimerRelicEvent: this.pityTimerRelicEvent,
			usedFights: this.usedFights.slice(),
			usedEvents: this.usedEvents.slice(),
			usedCelestials: this.usedCelestials.slice(),
			nextCelestialKey: this.nextCelestialKey,
			timeOnFloor: this.timeOnFloor,
			seedCountShop: this.seedCountShop,
			removeCountShop: this.removeCountShop,
			reroll: this.reroll,
			bundle: this.bundle,
			pendingCardChoiceEffects: this.pendingCardChoiceEffects ? Object.assign({}, this.pendingCardChoiceEffects) : null,
			minDeckSize: this.minDeckSize,
			entropy: this.entropy,
			addedBenevolence: this.addedBenevolence,
			convertedExpBonus: this.convertedExpBonus,
			plan: this.plan.slice()
		}
	}

	static build (src, broadcaster) {

		if (!User.canAccessRun(src))
			throw new Error("This run requires content that is not currently available.");
		let run = new Run();
		run.contentLimits = src.contentLimits || User.contentLimits(src.champion.model);
		run.power = src.power;
		run.broadcaster = broadcaster;
		run.difficultySetting = src.difficultySetting || "challenge";
		// Rebuilt fight cards can query champion passives, including cards in this deck.
		run.deck = src.deck.map(card => Object.assign({}, card));
		run.champion = Champion.build(run, src.champion);
		if (src.map) 
			run.map = Map.build(run, src.map);
		if (src.companion) 
			run.companion = Companion.build(run, src.companion);
		run.floor = src.floor;
		run.act = src.act || Object.values(Library.acts).find(act => act.act === run.floor)?.key;
		run.rooms = [];
		run.rooms = src.rooms.map(room => Room.build(run, room));
		run.levelingUp = src.levelingUp;
		run.choosingCard = src.choosingCard;
		run.droppingCard = src.droppingCard;
		run.duplicatingCard = src.duplicatingCard;
		run.storedCardChoice = src.storedCardChoice
		run.cardChance = src.cardChance;
		run.eventChance = src.eventChance;
		run.maxFloor = 3;
		run.storyEvent = src.storyEvent;
		run.memory = src.memory;
		run.background = src.background;
		run.audio = src.audio;
		run.roomsCleared = src.roomsCleared;
		run.bossesSlain = src.bossesSlain;
		//run.insight = src.insight;
		//run.removes = src.removes;
		run.paws = src.paws;
		run.keys = src.keys;
		run.time = src.time;
		run.startingFloor = src.startingFloor;
		run.campfireChoices = src.campfireChoices;
		run.pathChoices = src.pathChoices;
		run.droppingPotion = src.droppingPotion;
		run.droppingRelic = src.droppingRelic;
		run.quest = src.quest;
		run.cardChoiceCount = src.cardChoiceCount;
		run.pityTimerRare = src.pityTimerRare;
		run.pityTimerPotion = src.pityTimerPotion;
		run.pityTimerRelicEvent = src.pityTimerRelicEvent;
		run.usedFights = src.usedFights;
		run.usedEvents = src.usedEvents || [];
		run.usedCelestials = src.usedCelestials || [];
		run.nextCelestialKey = src.nextCelestialKey;
		run.timeOnFloor = src.timeOnFloor;
		run.reroll = src.reroll;
		run.bundle = src.bundle;
		run.pendingCardChoiceEffects = src.pendingCardChoiceEffects ? Object.assign({}, src.pendingCardChoiceEffects) : null;
		run.seedCountShop = src.seedCountShop;
		run.removeCountShop = src.removeCountShop;
		run.minDeckSize = src.minDeckSize;
		run.entropy = src.entropy;
		run.addedBenevolence = src.addedBenevolence;
		run.convertedExpBonus = src.convertedExpBonus || 0;
		run.plan = src.plan ? src.plan.slice() : [];
		if (src.options)
			run.options = src.options.map(option => run.getUpgradedCard(option, true));
		return run;
	}
}
